/*
 * Nomes de mês e de dia da semana por idioma — dado de IDIOMA do módulo de
 * datas, não copy do host. Tabelas fixas, e não `Intl`, de propósito: são só
 * três idiomas, e o ICU muda a saída entre engines e versões (o ponto de
 * "set.", o "de" do formato combinado, a caixa), o que quebrava testes e
 * desenhos. Um idioma novo entra aqui.
 */

export type DateLocale = 'pt-BR' | 'en' | 'es';

/** Qualquer tag BCP 47 (`pt-BR`, `en-US`, `es_419`) → um dos três idiomas. Só
 *  a língua importa; o que não for inglês nem espanhol cai no português, o
 *  padrão do produto. */
export function dateLocale(locale: string | null | undefined): DateLocale {
  const language = locale?.replaceAll('_', '-').toLowerCase().split('-')[0];
  if (language === 'en') return 'en';
  if (language === 'es') return 'es';
  return 'pt-BR';
}

type Names = {
  /** Na caixa do idioma, sem ponto: "set" (pt), "Sep" (en), "sept" (es). */
  readonly monthShort: readonly string[];
  /** Na caixa do idioma: "setembro", "September", "septiembre". */
  readonly monthLong: readonly string[];
  /** Domingo primeiro, com inicial maiúscula: abre rótulos ("Seg, 14 set"). */
  readonly weekdayShort: readonly string[];
  /** Uma letra, maiúscula — o cabeçalho da grade do calendário. O espanhol usa
   *  "X" na quarta, como o CLDR, para não repetir o "M" da terça. */
  readonly weekdayNarrow: readonly string[];
  /** Por extenso, na caixa do idioma — o nome acessível da coluna da grade. */
  readonly weekdayLong: readonly string[];
};

const NAMES: Record<DateLocale, Names> = {
  'pt-BR': {
    monthShort: [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ],
    monthLong: [
      'janeiro',
      'fevereiro',
      'março',
      'abril',
      'maio',
      'junho',
      'julho',
      'agosto',
      'setembro',
      'outubro',
      'novembro',
      'dezembro',
    ],
    weekdayShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    weekdayNarrow: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
    weekdayLong: [
      'domingo',
      'segunda-feira',
      'terça-feira',
      'quarta-feira',
      'quinta-feira',
      'sexta-feira',
      'sábado',
    ],
  },
  en: {
    monthShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    monthLong: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    weekdayShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    weekdayNarrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    weekdayLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  },
  es: {
    monthShort: [
      'ene',
      'feb',
      'mar',
      'abr',
      'may',
      'jun',
      'jul',
      'ago',
      'sept',
      'oct',
      'nov',
      'dic',
    ],
    monthLong: [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ],
    weekdayShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    weekdayNarrow: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    weekdayLong: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  },
};

function pick(list: readonly string[], index: number): string {
  return list[index] ?? '';
}

/** O mês de `date` (0–11 via `getMonth`), curto. */
export function monthShort(date: Date, locale: string | null | undefined): string {
  return pick(NAMES[dateLocale(locale)].monthShort, date.getMonth());
}

export function monthLong(date: Date, locale: string | null | undefined): string {
  return pick(NAMES[dateLocale(locale)].monthLong, date.getMonth());
}

/** O dia da semana de `date` (via `getDay`), curto e com inicial maiúscula. */
export function weekdayShort(date: Date, locale: string | null | undefined): string {
  return pick(NAMES[dateLocale(locale)].weekdayShort, date.getDay());
}

export function weekdayNarrow(date: Date, locale: string | null | undefined): string {
  return pick(NAMES[dateLocale(locale)].weekdayNarrow, date.getDay());
}

export function weekdayLong(date: Date, locale: string | null | undefined): string {
  return pick(NAMES[dateLocale(locale)].weekdayLong, date.getDay());
}
